import { useSettings } from '@/contexts/SettingsContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Link } from 'react-router-dom';
import { Shield, CreditCard, Wrench, Truck, Star, Search, Users, Handshake, BadgeCheck, Heart, CheckCircle } from 'lucide-react';

export default function AboutPage() {
    const { settings } = useSettings();

    const workValues = [
        { icon: Search, title: 'Selección cuidadosa', desc: 'Solo unidades que merecen estar en nuestro stock.' },
        { icon: BadgeCheck, title: 'Transparencia total', desc: 'Te asesoramos y resolvemos cualquier duda desde el primer momento.' },
        { icon: Heart, title: 'Atención personalizada', desc: 'Buscamos el coche que mejor encaje contigo, tus necesidades y tu presupuesto.' },
        { icon: Handshake, title: 'Calidad-precio', desc: 'Nuestro compromiso es ofrecer la mejor relación calidad-precio posible.' },
    ];

    const services = [
        { icon: Shield, title: 'Garantía propia', desc: 'Todos nuestros vehículos cuentan con garantía propia para tu tranquilidad.' },
        { icon: CreditCard, title: 'Financiación a medida', desc: 'Estudiamos tu caso y te ofrecemos la mejor financiación.' },
        { icon: Wrench, title: 'Taller propio', desc: 'Cada vehículo pasa una revisión completa en nuestro taller antes de la entrega.' },
        { icon: Truck, title: 'Entrega a domicilio', desc: 'Te llevamos el coche donde estés, sin complicaciones.' },
    ];

    return (
        <div data-testid="about-page">
            {/* Hero */}
            <section className="py-14 sm:py-20">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <h1 className="font-[var(--font-display)] tracking-tight text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--brand-navy)] mb-6">
                            Quiénes somos
                        </h1>
                        <p className="text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed mb-4">
                            En <strong className="text-[hsl(var(--foreground))]">ASG Motor</strong> somos especialistas en vehículos de ocasión, y trabajamos con un objetivo claro: que comprar un coche sea un proceso <strong className="text-[hsl(var(--foreground))]">fácil, transparente y seguro</strong>.
                        </p>
                        <p className="text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
                            Nuestra experiencia en el sector y la confianza de nuestros clientes nos han permitido consolidarnos como una opción de referencia en la región. Seleccionamos cada vehículo con criterios de calidad, revisando su estado y cuidando cada detalle antes de ponerlo a la venta.
                        </p>
                    </div>
                </div>
            </section>

            {/* Nuestra forma de trabajar */}
            <section className="py-10 sm:py-14 bg-[var(--brand-gray-soft)]">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <h2 className="font-[var(--font-display)] tracking-tight text-2xl sm:text-3xl font-semibold mb-8">
                        Nuestra forma de trabajar
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {workValues.map((v, i) => (
                            <Card key={i} className="p-5 rounded-xl border bg-white shadow-sm flex gap-4">
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--brand-light-blue)] shrink-0">
                                    <v.icon className="h-5 w-5 text-[var(--brand-blue)]" />
                                </div>
                                <div>
                                    <h3 className="font-[var(--font-display)] font-semibold text-sm mb-1">{v.title}</h3>
                                    <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{v.desc}</p>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Nuestro compromiso */}
            <section className="py-10 sm:py-14">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <h2 className="font-[var(--font-display)] tracking-tight text-2xl sm:text-3xl font-semibold mb-4">
                            Nuestro compromiso
                        </h2>
                        <p className="text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed mb-4">
                            En ASG Motor creemos que la <strong className="text-[hsl(var(--foreground))]">confianza se gana con hechos</strong>. Por eso, nuestro trabajo no termina con la entrega: queremos que te vayas con la seguridad de haber tomado una buena decisión.
                        </p>
                        <p className="text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
                            Si estás buscando tu próximo coche, estaremos encantados de ayudarte.
                        </p>
                    </div>
                </div>
            </section>

            {/* Servicios */}
            <section className="py-10 sm:py-14 bg-[var(--brand-gray-soft)]">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <h2 className="font-[var(--font-display)] tracking-tight text-2xl sm:text-3xl font-semibold mb-8">
                        Nuestros servicios
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {services.map((s, i) => (
                            <Card key={i} className="p-5 rounded-xl border bg-white shadow-sm text-center hover:shadow-md transition-shadow duration-200">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--brand-light-blue)] mx-auto mb-3">
                                    <s.icon className="h-6 w-6 text-[var(--brand-blue)]" />
                                </div>
                                <h3 className="font-[var(--font-display)] font-semibold text-sm mb-1">{s.title}</h3>
                                <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{s.desc}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-10 sm:py-14">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <h2 className="font-[var(--font-display)] tracking-tight text-2xl sm:text-3xl font-semibold text-center mb-8">
                        Lo que dicen nuestros clientes
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {[
                            { name: 'María G.', text: 'Excelente trato y el coche en perfecto estado. Lo recomiendo 100%.', stars: 5 },
                            { name: 'Carlos P.', text: 'Me ayudaron con la financiación y todo fue muy rápido. Gran servicio.', stars: 5 },
                            { name: 'Ana R.', text: 'Muy profesionales. El coche llegó a casa tal y como prometieron.', stars: 5 },
                        ].map((t, i) => (
                            <Card key={i} className="p-5 rounded-xl border bg-white shadow-sm">
                                <div className="flex gap-0.5 mb-3">
                                    {Array.from({length: t.stars}).map((_, j) => (
                                        <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />
                                    ))}
                                </div>
                                <p className="text-sm text-[hsl(var(--muted-foreground))] italic leading-relaxed mb-3">"{t.text}"</p>
                                <p className="text-sm font-medium">{t.name}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-12 sm:py-16 bg-[var(--brand-gray-soft)]">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-[var(--font-display)] tracking-tight text-2xl sm:text-3xl font-semibold mb-4">
                        ¿Buscas tu próximo coche?
                    </h2>
                    <p className="text-[hsl(var(--muted-foreground))] mb-6 max-w-lg mx-auto">
                        Explora nuestro inventario o contacta con nosotros para que te ayudemos a encontrar el vehículo perfecto.
                    </p>
                    <div className="flex gap-3 justify-center">
                        <Button asChild className="bg-[var(--brand-navy)] hover:bg-[#0d3560] text-white">
                            <Link to="/coches">Ver vehículos</Link>
                        </Button>
                        <Button asChild variant="outline">
                            <Link to="/contacto">Contactar</Link>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
